import { useState } from 'react';

interface DesignSelectionProps {
  onSelect: (designId: number) => void;
}

const designs = [
  {
    id: 1,
    name: 'Neon Love',
    description: 'Киберпанк с неоновым свечением',
    gradient: 'from-purple-900 via-pink-900 to-purple-900',
    icon: 'ri-flashlight-fill',
    preview: 'https://public.readdy.ai/ai/img_res/011abe123d85bec29d8043bbc4faf819.jpg'
  },
  {
    id: 2,
    name: 'Soft Minimal',
    description: 'Нежный минимализм',
    gradient: 'from-pink-100 via-rose-100 to-pink-50',
    icon: 'ri-heart-3-fill',
    preview: 'https://readdy.ai/api/search-image?query=minimalist%20soft%20pastel%20pink%20cream%20background%20with%20single%20large%20elegant%20heart%20shape%20gentle%20shadow%20smooth%20clean%20design%20valentine%20card%20aesthetic%20simple%20modern%20style&width=400&height=500&seq=minimal1&orientation=portrait'
  },
  {
    id: 3,
    name: 'Funny Love',
    description: 'Мемная валентинка',
    gradient: 'from-yellow-300 via-orange-300 to-pink-300',
    icon: 'ri-emotion-laugh-fill',
    preview: 'https://readdy.ai/api/search-image?query=funny%20cute%20cartoon%20cats%20and%20emoji%20characters%20on%20bright%20colorful%20background%20playful%20humorous%20valentine%20card%20design%20with%20jumping%20smiling%20faces%20and%20hearts%20comic%20style%20illustration&width=400&height=500&seq=funny1&orientation=portrait'
  },
  {
    id: 4,
    name: 'Magic Motion',
    description: 'Анимированная магия',
    gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    icon: 'ri-magic-fill',
    preview: 'https://readdy.ai/api/search-image?query=magical%20animated%20background%20with%20floating%20hearts%20and%20sparkles%20glitter%20particles%20dynamic%20motion%20blur%20effect%20dreamy%20valentine%20card%20design%20with%20flowing%20energy%20and%20light%20trails&width=400&height=500&seq=magic1&orientation=portrait'
  },
  {
    id: 5,
    name: 'Dreamy Love',
    description: 'Сказочная фантазия',
    gradient: 'from-blue-200 via-purple-200 to-pink-200',
    icon: 'ri-cloud-fill',
    preview: 'https://readdy.ai/api/search-image?query=dreamy%20fantasy%20clouds%20with%20soft%20glowing%20hearts%20floating%20mini%20planets%20and%20rainbow%20in%20pastel%20sky%20fairytale%20valentine%20card%20design%20with%20twinkling%20stars%20and%20magical%20atmosphere&width=400&height=500&seq=dreamy1&orientation=portrait'
  }
];

export default function DesignSelection({ onSelect }: DesignSelectionProps) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-12">
        <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
          Выбери дизайн валентинки
        </h2>
        <p className="text-lg text-gray-400">
          Выбери, что отражает твои чувства! 💕
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {designs.map((design) => (
          <div
            key={design.id}
            onClick={() => onSelect(design.id)}
            onMouseEnter={() => setHoveredId(design.id)}
            onMouseLeave={() => setHoveredId(null)}
            className="group cursor-pointer"
          >
            <div className="bg-[#232E3C] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-[#2C3847] hover:border-[#2AABEE]">
              {/* Preview */}
              <div className={`relative w-full aspect-[4/5] bg-gradient-to-br ${design.gradient} overflow-hidden`}>
                {/* Animated elements based on design */}
                {design.id === 1 && (
                  <>
                    <div className="absolute top-10 left-10 animate-pulse">
                      <i className="ri-heart-fill text-5xl text-pink-400 opacity-60"></i>
                    </div>
                    <div className="absolute bottom-20 right-16 animate-pulse" style={{ animationDelay: '0.5s' }}>
                      <i className="ri-heart-fill text-4xl text-purple-400 opacity-60"></i>
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <i className="ri-heart-fill text-7xl text-pink-300 animate-ping"></i>
                    </div>
                  </>
                )}

                {design.id === 2 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="ri-heart-fill text-9xl text-rose-300 opacity-40 group-hover:scale-110 transition-transform duration-500"></i>
                  </div>
                )}

                {design.id === 3 && (
                  <>
                    <div className="absolute top-8 right-8 animate-bounce">
                      <i className="ri-emotion-happy-fill text-4xl text-orange-600"></i>
                    </div>
                    <div className="absolute bottom-12 left-12 animate-bounce" style={{ animationDelay: '0.3s' }}>
                      <i className="ri-emotion-laugh-fill text-5xl text-pink-600"></i>
                    </div>
                  </>
                )}

                {design.id === 4 && (
                  <>
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute animate-float"
                        style={{
                          left: `${20 + i * 15}%`,
                          top: `${10 + (i % 3) * 30}%`,
                          animationDelay: `${i * 0.3}s`
                        }}
                      >
                        <i className="ri-heart-fill text-3xl text-pink-300 opacity-50"></i>
                      </div>
                    ))}
                  </>
                )}

                {design.id === 5 && (
                  <>
                    <div className="absolute top-16 left-1/4 animate-float">
                      <i className="ri-cloud-fill text-5xl text-white opacity-60"></i>
                    </div>
                    <div className="absolute top-32 right-1/4 animate-float" style={{ animationDelay: '0.5s' }}>
                      <i className="ri-cloud-fill text-4xl text-white opacity-60"></i>
                    </div>
                    <div className="absolute bottom-20 left-1/2 -translate-x-1/2">
                      <i className="ri-heart-fill text-6xl text-pink-400 animate-pulse"></i>
                    </div>
                  </>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-2xl">
                      <i className="ri-check-line text-[#2AABEE] text-3xl"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 text-white group-hover:text-[#2AABEE] transition-colors duration-300">
                  {design.name}
                </h3>
                <p className="text-sm text-gray-400 mb-4">{design.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">{design.popularity}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info banner */}
      <div className="mt-12 max-w-2xl mx-auto">
        <div className="bg-[#232E3C] rounded-3xl p-6 border border-[#2C3847]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#2AABEE] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-information-fill text-white text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-white mb-2">Все дизайны бесплатны!</h4>
              <p className="text-sm text-gray-400">
                Выбери любой понравившийся дизайн и создай уникальную валентинку для своих друзей в Telegram 💌
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}